// Note: getCountryByDialCode is imported where needed in components
// This file focuses on validation logic only

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validate phone number format
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid
 */
export const validatePhone = (phone) => {
  if (!phone || typeof phone !== 'string') return false

  // Remove country code and spaces, then validate
  let cleanPhone = phone.trim()

  // Check if it's in the new format: +{dialCode}|{countryCode} {phoneNumber}
  const newFormatMatch = cleanPhone.match(/^\+(\d+)\|[a-z]{2}\s+(.+)$/)
  if (newFormatMatch) {
    cleanPhone = newFormatMatch[2] // Extract just the phone number part
  } else {
    // Check if it's in legacy format: +{dialCode} {phoneNumber}
    const legacyMatch = cleanPhone.match(/^\+(\d+)\s+(.+)$/)
    if (legacyMatch) {
      cleanPhone = legacyMatch[2] // Extract just the phone number part
    }
    // If no format matches, assume it's just the phone number
  }

  // Remove any remaining spaces and validate
  cleanPhone = cleanPhone.replace(/\s/g, '')
  return cleanPhone.length >= 7 && /^\d+$/.test(cleanPhone)
}

/**
 * Validate country format
 * @param {string} country - Country to validate
 * @returns {boolean} True if valid
 */
export const validateCountry = (country) => {
  if (!country || typeof country !== 'string') return false

  // Check if it matches the expected format: +{dialCode} {countryName}
  const countryMatch = country.match(/^\+(\d+)\s+(.+)$/)
  return countryMatch && countryMatch[1] && countryMatch[2]
}

/**
 * Validate required field
 * @param {*} value - Value to validate
 * @param {string} questionType - Type of question
 * @returns {boolean} True if valid
 */
export const validateRequired = (value, questionType) => {
  // Handle null, undefined, or empty values
  if (value === null || value === undefined || value === '') return false

  if (Array.isArray(value)) {
    return value.length > 0 && value.some((item) => item && item.trim() !== '')
  }

  if (typeof value === 'string') {
    return value.trim() !== ''
  }

  // For other types (numbers, booleans, objects), consider them valid if they exist
  return true
}

/**
 * Validate a single form field
 * @param {string} fieldKey - Field key
 * @param {*} value - Field value
 * @param {Object} answer - Answer object
 * @param {Object} questionDefinition - Question definition
 * @returns {Array|null} Array of error messages or null if valid
 */
export const validateField = (fieldKey, value, answer, questionDefinition) => {
  if (!questionDefinition) return null // Skip validation for deleted questions

  const errors = []

  // Required field validation
  if (questionDefinition.isRequired) {
    if (answer.answerType === 'object') {
      // Use subAnswers instead of subQuestions since answer is an answer object
      const subQuestions = answer.subAnswers || []
      const missingRequiredFields = subQuestions.filter((subQ) => {
        const subKey = subQ.subQuestionId
        const hasValue = value && value[subKey] && value[subKey].trim() !== ''
        return !hasValue
      })

      if (missingRequiredFields.length > 0) {
        errors.push('Please complete required field.')
      }
    } else {
      if (!validateRequired(value, answer.answerType)) {
        errors.push('Please complete required field.')
      }
    }
  }

  // Type-specific validation
  if (value && answer.answerType === 'email') {
    if (typeof value === 'string' && value.trim() && !validateEmail(value)) {
      errors.push('Please enter a valid email address.')
    }
  }

  if (value && answer.answerType === 'phone') {
    if (typeof value === 'string' && value.trim() && !validatePhone(value)) {
      errors.push('Please enter a valid phone number.')
    }
  }

  if (value && answer.answerType === 'country') {
    if (typeof value === 'string' && value.trim() && !validateCountry(value)) {
      errors.push('Please select a valid country.')
    }
  }

  if (value && answer.answerType === 'date') {
    if (typeof value === 'string' && value.trim()) {
      const date = new Date(value)
      if (isNaN(date.getTime())) {
        errors.push('Please enter a valid date.')
      }
    }
  }

  if (value && answer.answerType === 'scale') {
    if (typeof value === 'string' && value.trim()) {
      const scaleValue = parseInt(value)
      const maxScale = questionDefinition.scaleOptions?.maxScaleLimit || 6
      if (isNaN(scaleValue) || scaleValue < 1 || scaleValue > maxScale) {
        errors.push(`Please select a value between 1 and ${maxScale}.`)
      }
    }
  }

  return errors.length > 0 ? errors : null
}
